import {
  createContext,
  useReducer,
  useEffect,
  useRef,
  useCallback,
  useState,
} from 'react'
import PropTypes from 'prop-types'

import ChooseFiles from './choose-files'
import PreviewFiles from './preview-files'
import { getExif, getImageGeoJson } from '../../exif-utils'
import PreviewGeoTIFF from './preview-geotiff'
import bboxPolygon from '@turf/bbox-polygon'
import { reducer, createInitialState } from './reducer'
import { logException } from '../../logger'
import { createProject, updateProject, uploadProject } from './upload-project'
import proj4 from 'proj4'
import { feature } from '@turf/helpers'
import { pipelineProfiles } from './prop-types'
import { ApiFetchError } from '../../api'
import ModalOverlay, {
  ModalOverlayContent,
  ModalOverlayFooter,
} from '../ui/shared/overlays/modal-overlay'
import Button from '../ui/elements/button'
import { PaygPurchaseModal } from '../ui/shared/payg-purchase-modal'

const CreateProjectDispatch = createContext()

export default function CreateProject(props) {
  const {
    farms,
    mapboxToken,
    crops,
    fields,
    apiClient,
    units,
    pipelineProfiles,
    stripeToken,
    purchasesEnabled,
    currency,
  } = props
  const [showBuyCredits, setShowBuyCredits] = useState(false)

  const [state, dispatch] = useReducer(
    reducer.bind(null, {
      ...props,
      callbacks: { onBuyCredits: () => setShowBuyCredits(true) },
    }),
    props,
    createInitialState
  )

  const stateRef = useRef(state)
  stateRef.current = state
  const numberCaptures = state.captures && Object.keys(state.captures).length

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const updateCost = useCallback(_updateCost, [
    apiClient,
    numberCaptures,
    state.crop,
    // We want to force recalculation of costs when the
    // credits modal closes, so list its visibility as
    // a dependency
    showBuyCredits,
  ])
  useEffect(() => {
    updateCost()
  }, [updateCost])

  const beginUpload = useCallback(_beginUpload, [
    apiClient,
    pipelineProfiles,
    updateCost,
  ])

  useEffect(() => {
    if (state.status === 'uploading') {
      beginUpload()
    }
  }, [apiClient, stateRef, beginUpload, state.status])

  useEffect(() => {
    const newFiles = state.newFiles
    if (newFiles && newFiles.length > 0) {
      dispatch({ type: 'RESET_NEW_FILES' })
      dispatch({ type: 'BEGIN_UPDATE_METADATA' })

      let updatePromise
      if (state.projectType !== 'geotiff') {
        updatePromise = getExif(newFiles.map(id => state.files[id].file)).then(
          exif =>
            getImageGeoJson(exif, { allowEmptyGeometry: true }).then(
              geojson => {
                const { filteredExif, features } = filterImages(
                  exif,
                  geojson.features
                )
                geojson = { ...geojson, features }
                // Assign ids to features, since the id is not from EXIF
                geojson.features.forEach((f, i) => {
                  f.id = newFiles[i]
                })

                if (geojson.features.length > 0) {
                  dispatch({
                    type: 'UPDATE_METADATA',
                    payload: { exif: filteredExif, geojson },
                  })
                  dispatch({
                    type: 'SET_PROCESSING_OPTION',
                    payload: {
                      key: 'bands',
                      value: geojson.features[0].properties.bands,
                    },
                  })
                }
              }
            )
        )
      } else {
        updatePromise = getGeoTIFFMetadata(
          state.files[newFiles[0]].file,
          newFiles[0]
        )
      }

      updatePromise.finally(() =>
        dispatch({ type: 'FINISHED_UPDATE_METADATA' })
      )
    }
  }, [state.files, state.newFiles, state.projectType])

  useEffect(() => {
    const handler = e => {
      if (
        !window.__ALLOW_LEAVE_PAGE__ &&
        state.status !== 'success' &&
        state.files &&
        Object.keys(state.files).length > 0
      ) {
        e.preventDefault()
        e.returnValue = ''
      }
    }

    window.addEventListener('beforeunload', handler)

    return () => {
      window.removeEventListener('beforeunload', handler)
    }
  }, [state.status, state.files])

  const [title, warning, dismissWarning] = useWarning(state.options)
  const message =
    state.files && state.validationMessage
      ? state.validationMessage
      : state.numberDiscardedFiles
      ? `${state.numberDiscardedFiles} of the selected images were not part of the initial upload and have been removed`
      : null

  return (
    <CreateProjectDispatch.Provider value={dispatch}>
      {(!state.files && <ChooseFiles projectType={state.projectType} />) ||
        (state.projectType !== 'geotiff' ? (
          <PreviewFiles
            files={state.files}
            captures={state.captures}
            allSelected={state.allSelected}
            mapboxToken={mapboxToken}
            lastSelected={state.lastSelected}
            metaData={state.metaData}
            metadataInitializing={state.metadataProgressCount > 0}
            farms={farms}
            farmId={state.farmId}
            fields={fields}
            fieldName={state.fieldName}
            crop={state.crop}
            crops={crops}
            status={state.status}
            units={units}
            projectId={state.projectId}
            projectType={state.projectType}
            options={state.options}
            cameraProperties={state.cameraProperties}
            gcpEnabled={state.permissions.gcp}
            cost={state.cost}
            validationMessage={state.validationMessage}
            isMissingFiles={state.isMissingFiles}
            hasMetaDataErrors={state.hasMetaDataErrors}
            apiClient={apiClient}
            stripeToken={stripeToken}
            purchasesEnabled={purchasesEnabled}
            currency={currency}
            allowChanges={state.projectId == null}
          />
        ) : (
          <PreviewGeoTIFF
            files={state.files}
            metaData={state.metaData}
            mapboxToken={mapboxToken}
            farms={farms}
            farmId={state.farmId}
            fields={fields}
            fieldName={state.fieldName}
            crop={state.crop}
            crops={crops}
            status={state.status}
            units={units}
            projectId={state.projectId}
            projectType={state.projectType}
            options={state.options}
            cameraProperties={state.cameraProperties}
            validationMessage={state.validationMessage}
            isMissingFiles={state.isMissingFiles}
            gcpEnabled={state.permissions.gcp}
            apiClient={apiClient}
            stripeToken={stripeToken}
            purchasesEnabled={purchasesEnabled}
            uploadedBytes={state.uploadedBytes}
            totalBytes={state.totalBytes}
            canAddNew={state.canAddNew}
          />
        ))}
      {showBuyCredits && (
        <PaygPurchaseModal
          apiClient={apiClient}
          show={true}
          stripeToken={stripeToken}
          currency={currency}
          onDismiss={() => setShowBuyCredits(false)}
        />
      )}
      {message && (
        <div className="tw-absolute tw-bottom-5 tw-w-full tw-z-messages tw-pointer-events-none">
          <div className="tw-mx-auto tw-border-b-4 tw-border-secondary tw-max-w-md tw-bg-white tw-p-4 tw-rounded tw-shadow-lg tw-flex tw-flex-row tw-items-center tw-pointer-events-auto">
            <div>{message}</div>
          </div>
        </div>
      )}
      {warning ? (
        <ModalOverlay title={title} show onDismiss={dismissWarning}>
          <ModalOverlayContent>{warning}</ModalOverlayContent>
          <ModalOverlayFooter>
            <Button size="large" action="primary" onClick={dismissWarning}>
              Ok
            </Button>
          </ModalOverlayFooter>
        </ModalOverlay>
      ) : null}
    </CreateProjectDispatch.Provider>
  )

  async function _beginUpload() {
    const state = stateRef.current

    let projectId = state.projectId
    try {
      let lastTouch = 0
      const progressCallback =
        state.projectType !== 'geotiff'
          ? (file, status) => {
              dispatch({
                type: 'SET_FILE_UPLOAD_STATUS',
                payload: { id: file.name, status },
              })
              try {
                const now = new Date().getTime()
                if (now - lastTouch > 30 * 1000) {
                  apiClient.project
                    .touch(projectId)
                    .catch(err => logException(err))
                  lastTouch = now
                }
              } catch (err) {
                logException(err)
              }
            }
          : (uploadedBytes, totalBytes) => {
              dispatch({
                type: 'SET_FILE_UPLOAD_PROGRESS',
                payload: { uploadedBytes, totalBytes },
              })
              try {
                const now = new Date().getTime()
                if (now - lastTouch > 30 * 1000) {
                  apiClient.project
                    .touch(projectId)
                    .catch(err => logException(err))
                  lastTouch = now
                }
              } catch (err) {
                logException(err)
              }
            }
      const uploadState = {
        ...state,
        apiClient,
        farmId: state.farmId,
        progressCallback,
        pipelineProfiles,
      }

      if (!projectId) {
        projectId = await createProject(uploadState)
        dispatch({ type: 'SET_PROJECT_ID', payload: projectId })
      } else {
        await updateProject({ ...uploadState, projectId })
      }

      await uploadProject({
        ...uploadState,
        projectId,
        pipelineProfiles,
      })

      dispatch({ type: 'SUCCESS_UPLOAD' })
    } catch (error) {
      if (error instanceof ApiFetchError) {
        if (error.response.status === 402) {
          dispatch({ type: 'ABORT_UPLOAD' })
          updateCost()
          return
        }
      }

      dispatch({ type: 'ERROR_UPLOAD' })
      logException(
        new ProjectUploadError(projectId, state.projectType, error),
        {
          project_type: state.projectType,
        }
      )
    }
  }

  function getGeoTIFFMetadata(file, id) {
    if (file) {
      return import('geotiff')
        .then(GeoTIFF => GeoTIFF.fromBlob(file))
        .then(tiff =>
          tiff.getImage().then(image => {
            const geoKeys = image.getGeoKeys()
            const epsg =
              (geoKeys &&
                (geoKeys.ProjectedCSTypeGeoKey ||
                  geoKeys.GeographicTypeGeoKey)) ||
              4326
            const projectedBbox = image.getBoundingBox()

            let bboxPromise
            if (epsg === 4326) {
              bboxPromise = Promise.resolve(projectedBbox)
            } else {
              bboxPromise = window
                .fetch(`https://epsg.io/${epsg}.proj4`)
                .then(res => res.text())
                .then(projDef => {
                  const projToWgs84 = proj4(projDef, 'EPSG:4326').forward
                  return [
                    ...projToWgs84([projectedBbox[0], projectedBbox[1]]),
                    ...projToWgs84([projectedBbox[2], projectedBbox[3]]),
                  ]
                })
                .catch(err => {
                  // Log error and hope it's WGS84
                  logException(err)
                  return projectedBbox
                })
            }

            return bboxPromise.then(bbox => {
              const bboxFeature = bboxPolygon(bbox, { id })
              bboxFeature.properties = {
                filename: file.name,
                width: image.getWidth(),
                height: image.getHeight(),
                tiePoints: image.getTiePoints(),
                samplesPerPixel: image.getSamplesPerPixel(),
                ...geoKeys,
                ...image.getGDALMetadata(),
                bands: guessGeoTIFFBands(image),
              }

              dispatch({
                type: 'UPDATE_METADATA',
                payload: {
                  geojson: {
                    type: 'FeatureCollection',
                    features: [bboxFeature],
                  },
                  exif: [],
                },
              })
            })
          })
        )
        .catch(err => {
          dispatch({
            type: 'UPDATE_METADATA',
            payload: {
              geojson: {
                type: 'FeatureCollection',
                features: [feature(null, { filename: file.name, error: err })],
              },
              exif: [],
            },
          })
          logException(
            new Error(`Failed to parse GeoTIFF: ${err.message}`, { cause: err })
          )
        })
    } else {
      return Promise.resolve()
    }
  }

  function _updateCost() {
    apiClient
      .getProjectCost(numberCaptures || 0, 0, state.crop)
      .then(cost => {
        dispatch({ type: 'SET_COST', payload: cost })
      })
      .catch(err => {
        logException(err)
        dispatch({ type: 'SET_COST', payload: null })
      })
  }
}

export { CreateProjectDispatch }

CreateProject.propTypes = {
  mapboxToken: PropTypes.string,
  farmId: PropTypes.number,
  farms: PropTypes.array,
  crops: PropTypes.array,
  fields: PropTypes.array,
  apiClient: PropTypes.shape({
    getProjectCost: PropTypes.func,
    subscriptions: PropTypes.shape({ create: PropTypes.func }),
    user: PropTypes.shape({
      defaultPaymentMethod: PropTypes.func,
      getPurchaseUrl: PropTypes.func,
    }),
    project: PropTypes.shape({
      touch: PropTypes.func,
    }),
    getProducts: PropTypes.func,
  }),
  units: PropTypes.string,
  permissions: PropTypes.object,
  project: PropTypes.object,
  uploadedFiles: PropTypes.func,
  pipelineProfiles,
  stripeToken: PropTypes.string,
  purchasesEnabled: PropTypes.bool,
  currency: PropTypes.string,
}

class ProjectUploadError extends Error {
  constructor(projectId, projectType, error) {
    super(
      `Project upload failed (#${projectId}/${projectType}): ${error.message}`
    )
    this.error = error
  }
}

function guessGeoTIFFBands(image) {
  const numberBands = image.getSamplesPerPixel()
  let bands
  switch (numberBands) {
    case 1:
      bands = ' '
      break
    case 2:
      bands = '  '
      break
    case 3:
      bands = 'rgb'
      break
    case 4:
      bands = 'rgba'
      break
    case 5:
      bands = 'grena'
      break
    case 6:
      bands = 'bgrena'
      break
    case 7:
      bands = 'bgrenta'
      break
    case 10:
      bands = 'vbgreEnNia'
      break
    case 11:
      bands = 'bBgGrReEnNa'
      break
    default:
      bands = 'rgb'.substring(0, numberBands)
  }

  return bands
}

const warnings = [
  {
    key: 'expressUpload',
    title: 'About Express Upload',
    message: (
      <div className="tw-flex tw-flex-col tw-space-y-2">
        <p>
          The Express Upload option scales down the images to reduce the size of
          the images and the time it takes to upload them.
        </p>
        <p>
          As a result, the orthomosaic resolution will be noticeably lower
          compared to uploading images without Express Upload.
        </p>
        <p>
          It is recommended to not use this option for use cases like plant
          counts where high resolution is important for the analysis.
        </p>
      </div>
    ),
  },
]
const storageKey = 'createProject/dismissedWarnings'

function useWarning(options) {
  const [dismissedWarnings, setDismissedWarnings] = useState(
    JSON.parse(window.localStorage.getItem(storageKey) || '{}')
  )
  useEffect(() => {
    window.localStorage.setItem(storageKey, JSON.stringify(dismissedWarnings))
  }, [dismissedWarnings])

  const warning = warnings.find(({ key }) =>
    options.some(option => option.key === key && option.active)
  )
  if (warning) {
    if (!dismissedWarnings[warning.key]) {
      return [
        warning.title,
        warning.message,
        () =>
          setDismissedWarnings({ ...dismissedWarnings, [warning.key]: true }),
      ]
    }
  }

  return [null, null, null]
}

function filterImages(exif, features) {
  if (exif.length !== features.length) {
    throw new Error(
      `Exif and features must be of the same length (${exif.length} != ${features.length})`
    )
  }
  const filteredExif = []
  const filteredFeatures = []
  for (let i = 0; i < features.length; i++) {
    const feature = features[i]
    if (isNadir(feature)) {
      filteredExif.push(exif[i])
      filteredFeatures.push(feature)
    } else {
      const error = { error: new Error('Image is not a nadir pose') }
      filteredExif.push(error)
      filteredFeatures.push({ ...feature, properties: error })
    }
  }

  return { exif: filteredExif, features: filteredFeatures }
}

/**
 * Returns true if the image feature's properties indicate it represents a image
 * taken in nadir (looking down) pose, _or_ if it can't be determined
 * @param {import('@turf/helpers').Feature} f
 * @returns {boolean} true if the image is in nadir pose or if it can't be determined
 */
function isNadir(f) {
  const pitch = f.properties?.camera_pitch
  return pitch == null || Number(pitch) < -70
}
